import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '', registration_number: '', vat_number: '', industry: '',
    email: '', phone: '', address: '', bee_status: '', tax_compliance_status: '', notes: ''
  });

  useEffect(() => { loadSuppliers(); }, []);

  const loadSuppliers = async () => {
    try {
      const { data } = await axios.get(`${API}/suppliers`, { withCredentials: true });
      setSuppliers(data);
    } catch (error) {
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/suppliers`, formData, { withCredentials: true });
      toast.success('Supplier created');
      setShowDialog(false);
      setFormData({ name: '', registration_number: '', vat_number: '', industry: '', email: '', phone: '', address: '', bee_status: '', tax_compliance_status: '', notes: '' });
      loadSuppliers();
    } catch (error) {
      toast.error('Failed to create supplier');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this supplier?')) return;
    try {
      await axios.delete(`${API}/suppliers/${id}`, { withCredentials: true });
      toast.success('Supplier deleted');
      loadSuppliers();
    } catch (error) {
      toast.error('Failed to delete supplier');
    }
  };

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="p-8" data-testid="suppliers-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}} data-testid="suppliers-title">Suppliers</h1>
          <p className="mt-2 text-zinc-600">Manage your suppliers and vendors</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="bg-black text-white hover:bg-zinc-800 gap-2" data-testid="add-supplier-button">
              <Plus size={18} /> Add Supplier
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Add Supplier</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Supplier Name</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="supplier-name-input" className="mt-2 border-zinc-300 hover:border-black transition-colors" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Registration #</Label>
                  <Input value={formData.registration_number} onChange={(e) => setFormData({...formData, registration_number: e.target.value})} data-testid="supplier-reg-input" className="mt-2 border-zinc-300 hover:border-black transition-colors" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">VAT #</Label>
                  <Input value={formData.vat_number} onChange={(e) => setFormData({...formData, vat_number: e.target.value})} data-testid="supplier-vat-input" className="mt-2 border-zinc-300 hover:border-black transition-colors" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Email</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} data-testid="supplier-email-input" className="mt-2 border-zinc-300 hover:border-black transition-colors" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Phone</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} data-testid="supplier-phone-input" className="mt-2 border-zinc-300 hover:border-black transition-colors" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Industry</Label>
                <Input value={formData.industry} onChange={(e) => setFormData({...formData, industry: e.target.value})} data-testid="supplier-industry-input" className="mt-2 border-zinc-300 hover:border-black transition-colors" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Address</Label>
                <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} data-testid="supplier-address-input" className="mt-2 border-zinc-300 hover:border-black transition-colors" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">BEE Status</Label>
                  <Input value={formData.bee_status} onChange={(e) => setFormData({...formData, bee_status: e.target.value})} data-testid="supplier-bee-input" className="mt-2 border-zinc-300 hover:border-black transition-colors" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Tax Compliance</Label>
                  <Input value={formData.tax_compliance_status} onChange={(e) => setFormData({...formData, tax_compliance_status: e.target.value})} data-testid="supplier-tax-input" className="mt-2 border-zinc-300 hover:border-black transition-colors" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Notes</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} data-testid="supplier-notes-input" className="mt-2 border-zinc-300 hover:border-black transition-colors" rows={3} />
              </div>
              <Button type="submit" className="w-full bg-black text-white hover:bg-zinc-800" data-testid="submit-supplier-button">Create Supplier</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-zinc-200">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="suppliers-table">
            <thead className="border-b border-zinc-200 bg-zinc-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Name</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Email</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Industry</th>
                <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-zinc-600">Total Purchases</th>
                <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-zinc-600">Outstanding POs</th>
                <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-zinc-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {suppliers.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-12 text-center text-zinc-500" data-testid="no-suppliers">No suppliers yet.</td></tr>
              ) : suppliers.map((s) => (
                <tr key={s.id} className="hover:bg-zinc-50 transition-colors" data-testid={`supplier-row-${s.id}`}>
                  <td className="px-6 py-4 font-medium text-zinc-900">{s.name}</td>
                  <td className="px-6 py-4 text-zinc-600 text-sm">{s.email || 'N/A'}</td>
                  <td className="px-6 py-4 text-zinc-600 text-sm">{s.industry || 'N/A'}</td>
                  <td className="px-6 py-4 text-right text-zinc-900 font-semibold">R {(s.total_purchases || 0).toLocaleString()}</td>
                  <td className="px-6 py-4 text-right text-zinc-600">{s.outstanding_pos || 0}</td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => handleDelete(s.id)} className="text-red-600 hover:text-red-800" data-testid={`delete-supplier-${s.id}`}><Trash size={18} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
